import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import {
  CreateFdCaseRequest,
  CreateFdCaseResponse,
  FdBusinessPartnerEmail,
  FdCancelCaseRequest,
  FdCaseDetail,
  FdCaseListQuery,
  FdCasePage,
  FdContact,
  FdDigPlanApprovalRequest,
  FdSaveDigPlanApprovalRequest,
  FdEquipment,
  FdHistoricalPage,
  FdSaveAfeReviewRequest,
  FdSaveBusinessPartnerEmailRequest,
  FdSaveContactRequest,
  FdSaveDigPlanExecutionRequest,
  FdSaveEamRequest,
  FdSaveEnvironmentalReviewRequest,
  FdSaveEquipmentRequest,
  FdSaveJobCloseOutRequest,
  FdSaveJobExecutionRequest,
  FdSaveLandReviewRequest,
  FdSaveProjectDataEntryRequest,
  FdSaveProjectPermittingRequest,
  FdSaveSchedulingRequest,
  FdSaveSiteScopingRequest,
  FdSaveWbsAllocationRequest,
  FdSaveWellRequest,
  FdSiteReviewDecisionRequest,
  FdStage,
  FdWbsAllocation,
  FdWell,
} from '#models';

/**
 * Mock implementation of the FD Cases API service.
 * Returns static fixture data so stage-page components can be developed and
 * demonstrated before the real API is available (Phase 4.1.2).
 * Replace by swapping the DI token in Phase 5.4 once the backend is wired up.
 */
@Injectable({
  providedIn: 'root',
})
export class FdCasesApiMockService {

  // ─── Fixture data ─────────────────────────────────────────────────────────────

  private readonly _mockCase: FdCaseDetail = {
    id: 1,
    workflowCaseId: 101,
    caseNumber: 'FD-2026-0001',
    stage: FdStage.ProjectDataEntry,
    isEnvironmentalReviewSubmitted: false,
    resolvedOutcome: null,
    cancellationReason: null,
    cancellationComments: null,
    facilityName: 'Test Facility Alpha',
    facilityId: 'FAC-001',
    chevNo: null,
    fot: 'CARLSBAD FMT',
    state: 'New Mexico',
    section: '14',
    township: '20S',
    range: '27E',
    survey: null,
    groundElevation: null,
    latitude: 32.5,
    longitude: -104.2,
    landOwnership: 'BLM',
    reasonForDecommission: 'End of productive life',
    directionsToFacility: 'Take Highway 285 north from Carlsbad.',
    wells: [
      { id: 1, wellName: 'State C 14-20 #1', api14: '30-015-00001-0000', fot: 'CARLSBAD FMT', area: 'CARLSBAD' },
    ],
    equipment: [
      { id: 1, description: 'Wellhead', count: 1, isReusable: false, comments: null },
    ],
    contacts: [
      { id: 1, name: 'Jane Smith', role: 'PA Coordinator', phoneNumber: '575-555-0100', email: 'jsmith@example.com' },
    ],
    wbsAllocations: [],
    businessPartnerEmails: [],
    landReview: null,
    environmentalReview: null,
    siteScoping: null,
    siteReview: null,
    afeReview: null,
    eam: null,
    projectPermitting: null,
    digPlan: null,
    scheduling: null,
    jobExecution: null,
    jobCloseOut: null,
    createdDate: '2026-07-21T08:00:00Z',
    createdBy: 'jsmith@example.com',
    updatedDate: '2026-07-21T08:00:00Z',
    updatedBy: 'jsmith@example.com',
  };

  // ─── Lookups ─────────────────────────────────────────────────────────────────

  getLookupOptions(_fieldKey: string): Observable<{ value: string }[]> {
    return of([]);
  }

  // ─── Queries ─────────────────────────────────────────────────────────────────

  getCases(_query?: FdCaseListQuery): Observable<FdCasePage> {
    return of({
      totalCount: 1,
      items: [
        {
          id: 1,
          caseNumber: 'FD-2026-0001',
          facilityName: 'Test Facility Alpha',
          fot: 'CARLSBAD FMT',
          stage: FdStage.ProjectDataEntry,
          isEnvironmentalReviewSubmitted: false,
          createdDate: '2026-07-21T08:00:00Z',
          updatedDate: '2026-07-21T08:00:00Z',
          createdBy: 'jsmith@example.com',
          scheduleCrew: null,
          startDate: null,
          projEndDate: null,
          projectedJobDays: null,
          deadlineDate: null,
          queueOrder: null,
        },
      ],
    });
  }

  getHistoricalCases(_pageNumber = 1, _pageSize = 25): Observable<FdHistoricalPage> {
    return of({ totalCount: 0, items: [] });
  }

  getCase(_id: number): Observable<FdCaseDetail> {
    return of(this._mockCase);
  }

  getCaseByCaseNumber(_caseNumber: string): Observable<FdCaseDetail> {
    return of(this._mockCase);
  }

  getAuditLogs(_id: number): Observable<unknown[]> {
    return of([]);
  }

  // ─── Mutations (all return void or fixture objects) ───────────────────────────

  createCase(_request: CreateFdCaseRequest): Observable<CreateFdCaseResponse> {
    return of({ id: 1, caseNumber: 'FD-2026-0001' });
  }

  cancelCase(_id: number, _request: FdCancelCaseRequest): Observable<void> {
    return of(undefined);
  }

  saveProjectDataEntry(_id: number, _request: FdSaveProjectDataEntryRequest): Observable<void> { return of(undefined); }
  submitProjectDataEntry(_id: number): Observable<void> { return of(undefined); }

  saveLandReview(_id: number, _request: FdSaveLandReviewRequest): Observable<void> { return of(undefined); }
  submitLandReview(_id: number): Observable<void> { return of(undefined); }

  saveEnvironmentalReview(_id: number, _request: FdSaveEnvironmentalReviewRequest): Observable<void> { return of(undefined); }
  submitEnvironmentalReview(_id: number): Observable<void> { return of(undefined); }

  saveSiteScoping(_id: number, _request: FdSaveSiteScopingRequest): Observable<void> { return of(undefined); }
  submitSiteScoping(_id: number): Observable<void> { return of(undefined); }

  siteReviewDecision(_id: number, _request: FdSiteReviewDecisionRequest): Observable<void> { return of(undefined); }

  saveAfeReview(_id: number, _request: FdSaveAfeReviewRequest): Observable<void> { return of(undefined); }
  submitAfeReview(_id: number): Observable<void> { return of(undefined); }

  saveEam(_id: number, _request: FdSaveEamRequest): Observable<void> { return of(undefined); }
  submitEam(_id: number): Observable<void> { return of(undefined); }

  saveProjectPermitting(_id: number, _request: FdSaveProjectPermittingRequest): Observable<void> { return of(undefined); }
  submitProjectPermitting(_id: number): Observable<void> { return of(undefined); }

  saveDigPlanCreation(_id: number): Observable<void> { return of(undefined); }
  submitDigPlanCreation(_id: number): Observable<void> { return of(undefined); }

  saveDigPlanApproval(_id: number, _request: FdSaveDigPlanApprovalRequest): Observable<void> { return of(undefined); }
  digPlanApproval(_id: number, _request: FdDigPlanApprovalRequest): Observable<void> { return of(undefined); }

  saveDigPlanExecution(_id: number, _request: FdSaveDigPlanExecutionRequest): Observable<void> { return of(undefined); }
  submitDigPlanExecution(_id: number): Observable<void> { return of(undefined); }

  saveScheduling(_id: number, _request: FdSaveSchedulingRequest): Observable<void> { return of(undefined); }
  submitScheduling(_id: number): Observable<void> { return of(undefined); }

  saveJobExecution(_id: number, _request: FdSaveJobExecutionRequest): Observable<void> { return of(undefined); }
  submitJobExecution(_id: number): Observable<void> { return of(undefined); }

  saveJobCloseOut(_id: number, _request: FdSaveJobCloseOutRequest): Observable<void> { return of(undefined); }
  submitJobCloseOut(_id: number): Observable<void> { return of(undefined); }

  // ─── Child rows ───────────────────────────────────────────────────────────────

  addWell(_id: number, request: FdSaveWellRequest): Observable<FdWell> {
    return of({ id: Date.now(), wellName: request.wellName, api14: request.api14, fot: request.fot, area: request.area });
  }
  deleteWell(_id: number, _wellId: number): Observable<void> { return of(undefined); }

  addEquipment(_id: number, request: FdSaveEquipmentRequest): Observable<FdEquipment> {
    return of({ id: Date.now(), description: request.description, count: request.count, isReusable: request.isReusable, comments: request.comments });
  }
  updateEquipment(_id: number, equipmentId: number, request: FdSaveEquipmentRequest): Observable<FdEquipment> {
    return of({ id: equipmentId, description: request.description, count: request.count, isReusable: request.isReusable, comments: request.comments });
  }
  deleteEquipment(_id: number, _equipmentId: number): Observable<void> { return of(undefined); }

  addContact(_id: number, request: FdSaveContactRequest): Observable<FdContact> {
    return of({ id: Date.now(), name: request.name, role: request.role, phoneNumber: request.phoneNumber, email: request.email });
  }
  updateContact(_id: number, contactId: number, request: FdSaveContactRequest): Observable<FdContact> {
    return of({ id: contactId, name: request.name, role: request.role, phoneNumber: request.phoneNumber, email: request.email });
  }
  deleteContact(_id: number, _contactId: number): Observable<void> { return of(undefined); }

  addWbsAllocation(_id: number, request: FdSaveWbsAllocationRequest): Observable<FdWbsAllocation> {
    return of({ id: Date.now(), wbsNumber: request.wbsNumber, allocationPercent: request.allocationPercent });
  }
  updateWbsAllocation(_id: number, wbsId: number, request: FdSaveWbsAllocationRequest): Observable<FdWbsAllocation> {
    return of({ id: wbsId, wbsNumber: request.wbsNumber, allocationPercent: request.allocationPercent });
  }
  deleteWbsAllocation(_id: number, _wbsId: number): Observable<void> { return of(undefined); }

  addBusinessPartnerEmail(_id: number, request: FdSaveBusinessPartnerEmailRequest): Observable<FdBusinessPartnerEmail> {
    return of({ id: Date.now(), email: request.email });
  }
  deleteBusinessPartnerEmail(_id: number, _emailId: number): Observable<void> { return of(undefined); }

  // ─── Attachments ──────────────────────────────────────────────────────────────

  getAttachments(_id: number): Observable<unknown[]> { return of([]); }
  getAttachmentDownloadUrl(_id: number, _attachmentId: number): Observable<{ downloadUrl: string }> {
    return of({ downloadUrl: '#' });
  }
  uploadAttachment(_id: number, _attachmentType: string, _file: File): Observable<unknown> { return of(null); }
  deleteAttachment(_id: number, _attachmentId: number): Observable<void> { return of(undefined); }
}
